import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Save, Upload, Trash2, KeyRound, ShieldAlert, LogOut, Lock, Crown } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import i18n from '@/i18n';

const API_URL = process.env.REACT_APP_API_URL;
const LOGO_MAX_BYTES = 500 * 1024;
const LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

export function CompanySettings() {
  const { t } = useTranslation('quotes');
  const { user, logout } = useAuth();
  const { isPro } = useQuoteEntitlement();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [pendingIndustry, setPendingIndustry] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNewConfirm, setPwNewConfirm] = useState('');

  const [profile, setProfile] = useState({
    company_name: '', company_email: '', company_phone: '', company_address: '',
    country_code: 'TR', currency: 'TRY', tax_label: 'KDV',
    timezone: 'Europe/Istanbul', unit_system: 'metric',
    interface_language: 'tr', default_quote_language: 'tr',
    quote_prefix: 'TEK', next_quote_number: 1,
    industry: '', logo_data_url: '', applied_templates: [],
    pdf_template: 'corporate_v2', pdf_palette: 'light_blue',
  });
  useEffect(() => {
    (async () => {
      try {
        const [p, ind] = await Promise.all([
          axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } }),
          axios.get(`${API_URL}/api/quote/industries`),
        ]);
        setProfile(p.data);
        setIndustries(ind.data?.industries || []);
        axios.get(`${API_URL}/api/quote/profile/logo`)
          .then(({ data }) => {
            if (data?.logo_data_url) setProfile((current) => ({ ...current, logo_data_url: data.logo_data_url }));
          })
          .catch(() => {});
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: profile.company_name,
        invoice_title: profile.invoice_title || '',
        tax_office: profile.tax_office || '',
        tax_number: profile.tax_number || '',
        company_email: profile.company_email,
        company_phone: profile.company_phone,
        company_address: profile.company_address,
        bank_name: profile.bank_name || '',
        iban: profile.iban || '',
        country_code: profile.country_code,
        timezone: profile.timezone,
        unit_system: profile.unit_system,
        interface_language: profile.interface_language,
        default_quote_language: profile.default_quote_language,
        industry: profile.industry || null,
        language: profile.language,
      };
      const { data } = await axios.put(`${API_URL}/api/quote/profile`, payload);
      setProfile(data);
      toast.success(t('companySettings.savedToast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  // Industry change: additive — ask before applying template
  const onIndustryChange = async (newKey) => {
    if (!newKey || newKey === profile.industry) {
      set({ industry: newKey });
      return;
    }
    // Save the industry first
    set({ industry: newKey });
    // Prompt user whether to add the starter catalog for the new sector
    const applied = (profile.applied_templates || []).includes(newKey);
    if (applied) return;
    setPendingIndustry(newKey);
  };

  const applyPendingTemplate = async () => {
    if (!pendingIndustry) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/profile/apply-template/${pendingIndustry}`);
      if (data.applied) {
        toast.success(t('settings.industryAppliedToast', { count: data.inserted }));
        set({ applied_templates: [...(profile.applied_templates || []), pendingIndustry] });
      } else {
        toast.info(t('settings.industryAlreadyApplied'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setPendingIndustry('');
    }
  };

  // --- Logo ---
  const onLogoPick = () => fileInputRef.current?.click();
  const onLogoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file after error
    if (!file) return;
    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      toast.error(t('settings.logoTypeError'));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(t('settings.logoSizeError'));
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/profile/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set({ logo_data_url: data.logo_data_url });
      toast.success(t('settings.logoUploadSuccess'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && typeof detail === 'object' && detail?.reason === 'logo_pro_only') {
        toast.error(detail.message || t('settings.logoProLockTitle'));
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || t('settings.logoUploadError')));
      }
    } finally {
      setUploading(false);
    }
  };
  const onLogoDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/quote/profile/logo`);
      set({ logo_data_url: '' });
      toast.success(t('settings.logoRemoved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  // --- Password change (uses legacy /api/profile) ---
  const changePassword = async () => {
    if (!pwCurrent) return toast.error(t('settings.passwordCurrentMissing'));
    if (!pwNew || pwNew.length < 8) return toast.error(t('settings.passwordTooShort'));
    if (pwNew !== pwNewConfirm) return toast.error(t('settings.passwordMismatch'));
    setChangingPw(true);
    try {
      await axios.put(`${API_URL}/api/profile`, {
        password: pwNew,
        current_password: pwCurrent,
      });
      setPwCurrent(''); setPwNew(''); setPwNewConfirm('');
      toast.success(t('settings.passwordUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.passwordUpdateError'));
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) {
    return (
      <QuoteLayout>
        <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
      </QuoteLayout>
    );
  }

  return (
    <QuoteLayout>
      <div className="max-w-3xl mx-auto pb-24" data-testid="company-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('companySettings.title')}</h1>
          <p className="text-sm text-[#595959] mt-1">{t('companySettings.subtitle')}</p>
        </div>

        {/* ---- USER ACCOUNT ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-1">{t('settings.user')}</h2>
          <p className="text-xs text-[#94A3B8] mb-4">{user?.email}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>{t('settings.userName')}</Label>
              <Input value={user?.name || ''} disabled data-testid="settings-user-name" />
              <p className="text-[11px] text-[#94A3B8] mt-1">{t('settings.userNameHint')}</p>
            </div>
          </div>
        </div>

        {/* ---- COMPANY ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4">{t('settings.sectionCompany')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>{t('settings.companyName')}</Label>
              <Input value={profile.company_name || ''} onChange={(e) => set({ company_name: e.target.value })} data-testid="settings-company-name" />
            </div>
            <div>
              <Label>{t('settings.companyEmail')}</Label>
              <Input type="email" value={profile.company_email || ''} onChange={(e) => set({ company_email: e.target.value })} />
            </div>
            <div>
              <Label>{t('settings.companyPhone')}</Label>
              <Input value={profile.company_phone || ''} onChange={(e) => set({ company_phone: e.target.value })} data-testid="settings-company-phone" />
            </div>
            <div className="md:col-span-2">
              <Label>{t('settings.companyAddress')}</Label>
              <Textarea rows={2} value={profile.company_address || ''} onChange={(e) => set({ company_address: e.target.value })} />
            </div>
            <div>
              <Label>{t('settings.country')}</Label>
              <Input maxLength={2} value={profile.country_code || 'TR'} onChange={(e) => set({ country_code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>{t('settings.industry')}</Label>
              <select
                value={profile.industry || ''}
                onChange={(e) => onIndustryChange(e.target.value)}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                data-testid="settings-industry-select"
              >
                <option value="">{t('settings.selectIndustry')}</option>
                {industries.map((ind) => (
                  <option key={ind.key} value={ind.key}>
                    {(profile.language || i18n.resolvedLanguage || i18n.language || 'tr').startsWith('tr')
                      ? ind.name
                      : (ind.name_en || ind.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4">{t('settings.billingInformation')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>{t('settings.invoiceTitle')}</Label><Input value={profile.invoice_title || ''} onChange={(e) => set({ invoice_title: e.target.value })} /></div>
            <div><Label>{t('settings.taxOffice')}</Label><Input value={profile.tax_office || ''} onChange={(e) => set({ tax_office: e.target.value })} /></div>
            <div><Label>{t('settings.taxNumber')}</Label><Input value={profile.tax_number || ''} maxLength={32} onChange={(e) => set({ tax_number: e.target.value })} /></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-payment-section">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-1">{t('settings.paymentDetails')}</h2>
          <p className="text-xs text-[#94A3B8] mb-4">{t('settings.paymentDetailsHint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>{t('settings.bankName')}</Label><Input value={profile.bank_name || ''} maxLength={100} onChange={(e) => set({ bank_name: e.target.value })} data-testid="bank-name-input" /></div>
            <div className="md:col-span-2"><Label>{t('settings.iban')}</Label><Input value={profile.iban || ''} maxLength={42} autoComplete="off" spellCheck={false} placeholder={t('settings.ibanPlaceholder')} onChange={(e) => set({ iban: e.target.value.toUpperCase() })} data-testid="iban-input" /></div>
          </div>
        </div>

        {/* ---- UYGULAMA DİLİ (P8) ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-language-section">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-1">
            {t('settings.languageSection')}
          </h2>
          <p className="text-xs text-[#94A3B8] mb-4">
            {t('settings.languageSectionHint')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl">
            {[
              { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
              { code: 'en', label: 'English', flag: '🇬🇧' },
              { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
              { code: 'fr', label: 'Français', flag: '🇫🇷' },
              { code: 'es', label: 'Español', flag: '🇪🇸' },
            ].map((l) => {
              const active = (profile.language || 'tr') === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={async () => {
                    if (active) return;
                    try {
                      const { data } = await axios.put(
                        `${API_URL}/api/quote/profile`,
                        { language: l.code },
                      );
                      setProfile(data);
                      await i18n.changeLanguage(l.code);
                      toast.success(t('settings.languageSaved', { defaultValue: t('common.saved') }));
                    } catch (err) {
                      toast.error(err.response?.data?.detail || t('settings.languageSaveError'));
                    }
                  }}
                  className={`rounded-xl border p-3 flex items-center gap-3 transition text-left ${
                    active
                      ? 'border-[#2563EB] ring-2 ring-[#2563EB]/25 bg-[#EFF6FF]'
                      : 'border-[#E5E5E5] hover:border-[#2563EB]/50 hover:bg-[#F8FAFC]'
                  }`}
                  data-testid={`language-option-${l.code}`}
                  aria-pressed={active}
                >
                  <span className="text-2xl">{l.flag}</span>
                  <div>
                    <div className="text-sm font-semibold text-[#1A1A2E]">{l.label}</div>
                    <div className="text-[11px] text-[#94A3B8] uppercase tracking-wider">{l.code}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ---- LOGO (Pro-only) ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-logo-section">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-[#1A1A2E]">{t('settings.logoSection')}</h2>
            {!isPro && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-semibold">Pro</span>
            )}
          </div>
          <p className="text-xs text-[#94A3B8] mb-4">
            {t('settings.logoHint')}
          </p>

          {isPro ? (
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl border border-dashed border-[#E5E5E5] bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="settings-logo-preview">
                {profile.logo_data_url
                  ? <img src={profile.logo_data_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                  : <span className="text-[11px] text-[#94A3B8]">{t('settings.logoNone')}</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file" accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={onLogoChosen} className="hidden"
                  data-testid="settings-logo-file"
                />
                <Button
                  onClick={onLogoPick} disabled={uploading}
                  className="bg-[#2563EB] hover:bg-[#1E4ED8] gap-2"
                  data-testid="settings-logo-upload"
                >
                  <Upload className="w-4 h-4" /> {uploading ? t('settings.logoUploading') : t('settings.logoUpload')}
                </Button>
                {profile.logo_data_url && (
                  <Button
                    variant="outline" onClick={onLogoDelete}
                    className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="settings-logo-delete"
                  >
                    <Trash2 className="w-4 h-4" /> {t('settings.logoDelete')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="settings-logo-pro-lock">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">{t('settings.logoProLockTitle')}</p>
                <p className="text-xs text-amber-800 mt-1">
                  {t('settings.logoProLockMessage')}
                  {profile.logo_data_url ? t('settings.logoProLockKept') : ''}
                </p>
                <Link to="/quote-upgrade" data-testid="settings-logo-upgrade-link">
                  <Button size="sm" className="mt-3 bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 text-white gap-2">
                    <Crown className="w-4 h-4" /> {t('settings.logoUpgradeCta')}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mb-8">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#2563EB] hover:bg-[#1E4ED8]"
            data-testid="company-settings-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('companySettings.savingButton') : t('companySettings.saveButton')}
          </Button>
        </div>

        {/* ---- PASSWORD ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-password-section">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-[#2563EB]" />
            <h2 className="text-lg font-semibold text-[#1A1A2E]">{t('settings.passwordSection')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t('settings.passwordCurrent')}</Label>
              <Input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} data-testid="settings-pw-current" />
            </div>
            <div>
              <Label>{t('settings.passwordNew')}</Label>
              <Input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="settings-pw-new" />
            </div>
            <div>
              <Label>{t('settings.passwordConfirm')}</Label>
              <Input type="password" value={pwNewConfirm} onChange={(e) => setPwNewConfirm(e.target.value)} data-testid="settings-pw-confirm" />
            </div>
          </div>
          <p className="text-xs text-[#94A3B8] mt-2">{t('settings.passwordHint')}</p>
          <div className="mt-3">
            <Button
              onClick={changePassword} disabled={changingPw}
              className="bg-[#2563EB] hover:bg-[#1E4ED8]"
              data-testid="settings-pw-submit"
            >
              {changingPw ? t('settings.passwordUpdating') : t('settings.passwordSubmit')}
            </Button>
          </div>
        </div>

        {/* ---- ACCOUNT / DANGER ZONE ---- */}
        <div className="bg-white rounded-2xl border border-red-100 p-6" data-testid="settings-danger-section">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-red-600" />
            <h2 className="text-lg font-semibold text-[#1A1A2E]">{t('settings.accountSection')}</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-[#E5E5E5]">
              <div>
                <p className="text-sm font-medium text-[#1A1A2E]">{t('settings.logoutTitle')}</p>
                <p className="text-xs text-[#94A3B8]">{t('settings.logoutHint')}</p>
              </div>
              <Button
                variant="outline" onClick={() => logout()}
                className="gap-2" data-testid="settings-logout-btn"
              >
                <LogOut className="w-4 h-4" /> {t('settings.logoutButton')}
              </Button>
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-red-200 bg-red-50">
              <div>
                <p className="text-sm font-medium text-red-800">{t('settings.deleteTitle')}</p>
                <p
                  className="text-xs text-red-700 mt-0.5"
                  dangerouslySetInnerHTML={{ __html: t('settings.deleteMessage') }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ---- Industry change confirm modal ---- */}
        {pendingIndustry && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="industry-apply-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-5">
              <h3 className="text-base font-semibold text-[#1A1A2E]">
                {t('settings.applyIndustryTitle')}
              </h3>
              <p className="text-sm text-[#595959] mt-2">
                {t('settings.applyIndustryMessage')}
              </p>
              <div className="flex gap-2 justify-end mt-4">
                <Button variant="outline" onClick={() => setPendingIndustry('')} data-testid="industry-apply-cancel">
                  {t('settings.applyIndustrySkip')}
                </Button>
                <Button onClick={applyPendingTemplate} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="industry-apply-confirm">
                  {t('settings.applyIndustryConfirm')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </QuoteLayout>
  );
}

export default CompanySettings;
